import { useTranslation } from 'react-i18next'
import { Menu, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon, GlobeAltIcon } from '@heroicons/react/outline'
import classNames from 'classnames'

const useFlags = typeof navigator === 'undefined' || !/Win/i.test(navigator.userAgent)

const languages = [
  { code: 'en', name: 'English', flag: useFlags ? '🇺🇸' : 'EN' },
  { code: 'ar', name: 'العربية', flag: useFlags ? '🇸🇦' : 'AR' },
]

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n, t } = useTranslation()

  // eslint-disable-next-line
  const currentLanguage = languages.find(lang => lang.code === i18n.language)

  const changeLanguage = languageCode => {
    i18n.changeLanguage(languageCode)
  }

  const changeLanguageLabel = t('common.changeLanguage')
  const languagePanelTitle = t('common.language')
  const isRTL = i18n.language === 'ar'

  return (
    <Menu as="div" className={classNames('relative text-left', compact ? 'z-[100] w-fit max-w-full' : 'inline-block w-full min-w-0')} style={{ zIndex: compact ? 100 : 1 }}>
      {compact ? (
        <Menu.Button
          type="button"
          className={classNames(
            'group inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full',
            'border border-gray-200/90 bg-white text-[1.35rem] leading-none shadow-sm',
            'transition-all duration-150 ease-out',
            'hover:border-gray-300 hover:bg-gray-50 hover:shadow',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-white',
            'active:scale-[0.97]',
            !useFlags && 'text-xs font-semibold tracking-wide'
          )}
          aria-label={changeLanguageLabel}
          title={changeLanguageLabel}
        >
          <span className="select-none" aria-hidden="true">
            {currentLanguage?.flag ?? '🌐'}
          </span>
        </Menu.Button>
      ) : (
        <Menu.Button
          type="button"
          className="inline-flex min-w-[200px] w-full items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100"
        >
          <GlobeAltIcon className="mr-2 h-4 w-4 flex-shrink-0" />
          <span className="mr-2 flex-shrink-0">{currentLanguage?.flag}</span>
          <span className="mr-2 min-w-0 flex-1" dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
            {currentLanguage?.name}
          </span>
          <ChevronDownIcon className="h-4 w-4 flex-shrink-0" />
        </Menu.Button>
      )}

      <Transition
        as="div"
        enter="transition ease-out duration-150"
        enterFrom="transform opacity-0 scale-[0.97] translate-y-0.5"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-[0.97]"
      >
        <Menu.Items
          className={classNames(
            'absolute w-64 overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-xl shadow-slate-900/8 ring-1 ring-black/[0.04] focus:outline-none',
            compact
              ? isRTL
                ? 'right-0 top-full z-[110] mt-1.5 origin-top-right'
                : 'left-0 top-full z-[110] mt-1.5 origin-top-left'
              : isRTL
              ? 'left-0 mt-1.5 origin-top-left'
              : 'right-0 mt-1.5 origin-top-right',
            !compact && 'z-10'
          )}
          style={{ zIndex: compact ? 110 : 10 }}
        >
          <div className="border-b border-indigo-100/80 bg-gradient-to-r from-indigo-50 via-white to-slate-50/90 px-3.5 py-2.5" dir={isRTL ? 'rtl' : 'ltr'}>
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-white/90 text-indigo-600 shadow-sm ring-1 ring-indigo-100">
                <GlobeAltIcon className="h-4 w-4" aria-hidden />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">{languagePanelTitle}</span>
            </div>
          </div>
          <div className="p-1.5">
            {languages.map(lang => {
              const selected = i18n.language === lang.code
              return (
                <Menu.Item key={lang.code}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => changeLanguage(lang.code)}
                      className={classNames(
                        'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-start text-sm transition-all',
                        active && !selected && 'bg-slate-50',
                        selected ? 'bg-indigo-50 font-semibold text-indigo-900 shadow-sm ring-1 ring-indigo-200/70' : 'text-slate-700 hover:bg-slate-50/90'
                      )}
                      dir={lang.code === 'ar' ? 'rtl' : 'ltr'}
                    >
                      <span
                        className={classNames(
                          'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-slate-200/80 bg-gradient-to-b from-white to-slate-50 shadow-sm',
                          useFlags ? 'text-lg' : 'text-xs font-semibold tracking-wide'
                        )}
                        aria-hidden="true"
                      >
                        {lang.flag}
                      </span>
                      <span className="min-w-0 flex-1 leading-snug">{lang.name}</span>
                      {selected ? <CheckIcon className="h-4 w-4 flex-shrink-0 text-indigo-600" aria-hidden="true" /> : null}
                    </button>
                  )}
                </Menu.Item>
              )
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}

export default LanguageSwitcher
